import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

import { ExpandIcon, PlayIcon } from '@/shared/icons'

import s from './CameraFrame.module.css'
import { Skeleton } from './Skeleton'

export interface FrameBox {
  top: string
  left: string
  width: string
  height: string
  color: string
  label: string
}

interface CameraFrameProps {
  /** Штамп времени в углу кадра; без него плашки нет (кадр распознавания показывается «чистым»). */
  stamp?: string
  camera: string
  /** Реальный снимок; без него рисуется заглушка. */
  snapshotUrl?: string | null
  /** Если задано — снимок кликабелен (открыть во весь экран). */
  onSnapshotClick?: () => void
  /** Видео, первый кадр которого показывается вместо снимка; если оно не загрузилось — снимок. */
  previewVideoUrl?: string
  box?: FrameBox
  /** Что показать в центре: номер автомобиля и т.п. */
  center?: ReactNode
  /** Показать кнопку воспроизведения. */
  playable?: boolean
  onPlay?: () => void
  /** Если задано — вместо снимка в кадре играет видео. */
  videoSrc?: string
  compact?: boolean
  /** Текст вместо недоступного медиа; по умолчанию формулировка ТЗ для событий. */
  unavailableText?: string
  /** Плашка REC в углу (по умолчанию есть). */
  rec?: boolean
  /** Иконка увеличения на кликабельном снимке (по умолчанию есть); сам клик от неё не зависит. */
  expandIcon?: boolean
}

/** Текст для любого недоступного медиа — один на снимок и видео (ТЗ п. 7.3). */
const MEDIA_UNAVAILABLE = 'Видео или фото события недоступно'

function InlineVideo({ src, stamp }: { src: string; stamp?: string }) {
  const [failed, setFailed] = useState(false)
  return (
    <>
      {failed ? (
        <div className={s.videoError}>{MEDIA_UNAVAILABLE}</div>
      ) : (
        <video
          className={s.video}
          src={src}
          controls
          autoPlay
          playsInline
          // Без кнопки скачивания и «картинки в картинке» в нативных контролах, без контекстного
          // меню «Сохранить видео как…». Это убирает скачивание из интерфейса, но не защищает
          // сам файл: адрес клипа виден в сетевых запросах.
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(e) => e.preventDefault()}
          onError={() => setFailed(true)}
        />
      )}
      {stamp && <span className={clsx(s.label, s.stamp)}>{stamp}</span>}
    </>
  )
}

export function CameraFrame({
  stamp,
  camera,
  snapshotUrl,
  onSnapshotClick,
  previewVideoUrl,
  box,
  center,
  playable,
  onPlay,
  videoSrc,
  compact,
  unavailableText = MEDIA_UNAVAILABLE,
  rec = true,
  expandIcon = true,
}: CameraFrameProps) {
  // Первый кадр клипа вместо снимка; если клип не загрузился, возвращаемся к снимку.
  const [failedPreview, setFailedPreview] = useState<string | null>(null)
  const preview = previewVideoUrl && failedPreview !== previewVideoUrl ? previewVideoUrl : undefined
  // Адрес превью ставим и снимаем сами: убранный из DOM <video> не прекращает загрузку, а бэкенд
  // не умеет Range, и клип, запущенный по кнопке, ждал бы, пока браузер отпустит тот же адрес.
  // Через атрибут src не выйдет: StrictMode переподключает ref, и React второй раз src не ставит.
  const previewRef = useCallback(
    (video: HTMLVideoElement | null) => {
      if (!video || !preview) return
      // Фрагмент #t заставляет браузер (в том числе Safari на iOS) отрисовать первый кадр без воспроизведения.
      video.src = `${preview}#t=0.001`
      return () => {
        video.removeAttribute('src')
        video.load()
      }
    },
    [preview],
  )
  // Снимок, который не удалось загрузить (бэкенд ответил ошибкой), или событие вовсе без медиа:
  // показываем заглушку с текстом по ТЗ и не предлагаем открыть снимок во весь экран.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const snapshotFailed = Boolean(snapshotUrl) && failedUrl === snapshotUrl
  const snapshot = snapshotFailed || preview ? undefined : snapshotUrl
  // Пока снимок грузится (кадр распознавания весит ~500 КБ), поверх кадра — шиммер.
  // Снимок из кеша может оказаться готовым раньше, чем сработает onLoad, поэтому проверяем complete.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    const element = imgRef.current
    if (snapshot && element?.complete && element.naturalWidth > 0) setLoadedUrl(snapshot)
  }, [snapshot])
  const media = preview ?? snapshot
  const snapshotLoading = Boolean(media) && loadedUrl !== media
  const image = preview ? (
    <video
      ref={previewRef}
      className={s.image}
      muted
      playsInline
      preload="metadata"
      aria-hidden="true"
      onLoadedData={() => setLoadedUrl(preview)}
      onError={() => setFailedPreview(preview)}
    />
  ) : snapshot ? (
    <img
      ref={imgRef}
      className={s.image}
      src={snapshot}
      alt=""
      onLoad={() => setLoadedUrl(snapshot)}
      onError={() => setFailedUrl(snapshot)}
    />
  ) : null

  if (videoSrc) {
    return (
      <div className={clsx(s.frame, compact && s.compact)}>
        {/* key сбрасывает состояние ошибки при смене видео */}
        <InlineVideo key={videoSrc} src={videoSrc} stamp={stamp} />
      </div>
    )
  }

  return (
    <div className={clsx(s.frame, compact && s.compact)} aria-busy={snapshotLoading || undefined}>
      {image && onSnapshotClick ? (
        <button
          type="button"
          className={s.imageButton}
          title="Открыть снимок во весь экран"
          aria-label="Открыть снимок во весь экран"
          onClick={onSnapshotClick}
        >
          {image}
          {expandIcon && (
            <span className={s.expand}>
              <ExpandIcon size={14} />
            </span>
          )}
        </button>
      ) : (
        (image ?? <div className={s.scan} />)
      )}
      {/* Шиммер над снимком до его загрузки; подписи ниже в разметке, поэтому остаются поверх. */}
      {snapshotLoading && <Skeleton className={s.loading} width="100%" height="100%" />}
      {(snapshotFailed || !snapshotUrl) && !playable && !center && (
        <div className={s.notice}>{unavailableText}</div>
      )}
      {stamp && <span className={clsx(s.label, s.stamp)}>{stamp}</span>}
      {rec && <span className={clsx(s.label, s.rec)}>REC</span>}
      {box && (
        <div
          className={s.box}
          style={
            {
              top: box.top,
              left: box.left,
              width: box.width,
              height: box.height,
              color: box.color,
            } as CSSProperties
          }
        >
          <span className={s.tag}>{box.label}</span>
        </div>
      )}
      {playable && (
        <button
          type="button"
          className={clsx(s.center, s.play)}
          title="Воспроизвести видео"
          onClick={onPlay}
        >
          <PlayIcon size={20} />
        </button>
      )}
      {center && <div className={s.center}>{center}</div>}
      {camera && <span className={clsx(s.label, s.camera)}>{camera}</span>}
    </div>
  )
}
